import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import { t } from 'i18next';
import { useContext, useRef } from 'react';

import DescribedMenuItem from '~components/Menu/DescribedMenuItem';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import {
  CHARGES_SETTLED_SIDES,
  CHARGES_SHOW_OPTIONS,
  ChargesGrainTab,
  ChargesSettledSide,
  ChargesShowOption,
  ChargesViewTab,
  deriveIssueFilters,
  getShownOption,
} from '../approveChargesTabs';

const showOptionKeys = {
  [ChargesShowOption.ANY]: {
    description: 'approvals.approve_charges.show.descriptions.any',
    label: 'approvals.approve_charges.show.options.all_loads',
  },
  [ChargesShowOption.APPROVED]: {
    description: 'approvals.approve_charges.show.descriptions.approved',
    label: 'approvals.approve_charges.show.options.approved',
  },
  [ChargesShowOption.NEEDS_REVIEW]: {
    description: 'approvals.approve_charges.show.descriptions.needs_review',
    label: 'approvals.approve_charges.show.options.needs_review',
  },
  [ChargesShowOption.UNAPPROVED]: {
    description: 'approvals.approve_charges.show.descriptions.unapproved',
    label: 'approvals.approve_charges.show.options.unapproved',
  },
} satisfies Record<ChargesShowOption, { description: string; label: string }>;

const settledSideKeys = {
  [ChargesSettledSide.BOTH]: {
    description: 'approvals.approve_charges.show.settled_descriptions.both',
    label: 'approvals.approve_charges.show.settled_options.both',
  },
  [ChargesSettledSide.PAYABLE]: {
    description: 'approvals.approve_charges.show.settled_descriptions.payable',
    label: 'approvals.approve_charges.show.settled_options.payable',
  },
  [ChargesSettledSide.RECEIVABLE]: {
    description: 'approvals.approve_charges.show.settled_descriptions.receivable',
    label: 'approvals.approve_charges.show.settled_options.receivable',
  },
} satisfies Record<ChargesSettledSide, { description: string; label: string }>;

export default function ChargesShowMenu() {
  const {
    exception,
    grainTab,
    issueTypes,
    selectSettledSide,
    selectShowOption,
    settledSide,
    showOption,
    viewTab,
  } = useContext(ApproveChargesPageContext);
  const { applyFilters } = useContext(FilesQueryContext);
  const menu = useRef<MenuHandler>(null);

  const shownOption = getShownOption({ exception, showOption }, issueTypes);
  const isSettledTab = viewTab === ChargesViewTab.SETTLED;
  const getShowOptionLabel = (option: ChargesShowOption) =>
    option === ChargesShowOption.ANY && grainTab === ChargesGrainTab.HOURLY_JOBS
      ? t('approvals.approve_charges.show.options.all_jobs')
      : t(showOptionKeys[option].label);
  const label = isSettledTab
    ? t(settledSideKeys[settledSide].label)
    : getShowOptionLabel(shownOption);

  const handleSelectShowOption = (option: ChargesShowOption) => {
    if (shownOption === ChargesShowOption.NEEDS_REVIEW) {
      applyFilters(deriveIssueFilters(false, issueTypes));
    }

    selectShowOption(option);
    menu.current?.onClose?.();
  };

  const handleSelectSettledSide = (side: ChargesSettledSide) => {
    selectSettledSide(side);
    menu.current?.onClose?.();
  };

  return (
    <Menu
      ref={menu}
      menuTrigger={
        <SmallButton color="inherit" endIcon={<ArrowDropDown />} variant="outlined">
          {t('approvals.approve_charges.show.label', { option: label })}
        </SmallButton>
      }
    >
      {isSettledTab
        ? CHARGES_SETTLED_SIDES.map((side) => (
            <DescribedMenuItem
              key={side}
              description={t(settledSideKeys[side].description)}
              label={t(settledSideKeys[side].label)}
              onClick={() => handleSelectSettledSide(side)}
              selected={side === settledSide}
            />
          ))
        : CHARGES_SHOW_OPTIONS.map((option) => (
            <DescribedMenuItem
              key={option}
              description={t(showOptionKeys[option].description)}
              label={getShowOptionLabel(option)}
              onClick={() => handleSelectShowOption(option)}
              selected={option === shownOption}
            />
          ))}
    </Menu>
  );
}
